(function (root, factory) {
  const existing = root && root.GLLFCartAuthority;
  if (existing && existing.contractVersion === "gllf.cart-authority.v2") {
    if (typeof module === "object" && module.exports) module.exports = existing;
    if (typeof existing.installVeenaHeaderSync === "function") existing.installVeenaHeaderSync();
    return;
  }
  const api = factory(root);
  if (typeof module === "object" && module.exports) module.exports = api;
  root.GLLFCartAuthority = api;
})(typeof window !== "undefined" ? window : globalThis, function (root) {
  "use strict";

  const inFlight = new Map();
  const authoritativeProtectionProductIds = new Set(["10405563990312", "10405571461416"]);
  let verifiedCartState = null;
  const property = (line, name) => String((line && line.properties && line.properties[name]) || "");
  const money = (cart) => Number(cart && cart.total_price || 0);
  const numericId = (value) => String(value == null ? "" : value).split("/").pop();
  const deepCopy = (value) => JSON.parse(JSON.stringify(value == null ? null : value));

  function canonical(value) {
    if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
    if (value && typeof value === "object") {
      return `{${Object.keys(value).filter((key) => value[key] !== undefined).sort().map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`).join(",")}}`;
    }
    return JSON.stringify(value);
  }

  // This digest is an identity/version marker, not a security signature.
  // Server-signed quote, selection and order evidence remains authoritative.
  function digest(value) {
    const text = typeof value === "string" ? value : canonical(value);
    let a = 0x811c9dc5;
    let b = 0x9e3779b9;
    for (let index = 0; index < text.length; index += 1) {
      const code = text.charCodeAt(index);
      a ^= code;
      a = Math.imul(a, 0x01000193) >>> 0;
      b ^= code + index;
      b = Math.imul(b, 0x85ebca6b) >>> 0;
    }
    return `${a.toString(16).padStart(8, "0")}${b.toString(16).padStart(8, "0")}`;
  }

  function sortedObject(value) {
    const output = {};
    Object.keys(value || {}).sort().forEach((key) => {
      const current = value[key];
      if (current !== undefined && current !== null && String(current) !== "") output[key] = String(current);
    });
    return output;
  }

  function lineProjection(line, includeKey) {
    const sellingPlan = line && line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
    return {
      ...(includeKey ? { key: String(line && line.key || "") } : {}),
      productId: numericId(line && line.product_id),
      variantId: numericId(line && (line.variant_id || line.id)),
      quantity: Number(line && line.quantity || 0),
      originalLinePrice: Number(line && (line.original_line_price != null ? line.original_line_price : line.final_line_price) || 0),
      finalLinePrice: Number(line && (line.final_line_price != null ? line.final_line_price : line.original_line_price) || 0),
      sellingPlanId: numericId(sellingPlan && sellingPlan.id || line && line.selling_plan),
      properties: sortedObject(line && line.properties),
    };
  }

  function cartProjection(cart, includeKeys) {
    const items = (cart && cart.items || []).map((line) => lineProjection(line, includeKeys !== false));
    items.sort((left, right) => canonical(left).localeCompare(canonical(right)));
    return {
      token: String(cart && (cart.token || cart.id) || ""),
      currency: String(cart && cart.currency || ""),
      items,
      attributes: sortedObject(cart && cart.attributes),
    };
  }

  const cartFingerprint = (cart) => `gllf-cart-v2-${digest(cartProjection(cart, true))}`;
  const cartSemanticFingerprint = (cart) => `gllf-cart-semantic-v2-${digest(cartProjection(cart, false))}`;

  function rememberVerifiedCart(fingerprint, validUntil) {
    fingerprint = String(fingerprint || "");
    if (!fingerprint) return null;
    const until = Number(validUntil || 0);
    const normalizedUntil = Number.isFinite(until) && until > 0 ? until : 0;
    if (verifiedCartState && verifiedCartState.fingerprint === fingerprint && verifiedCartState.validUntil === normalizedUntil) return verifiedCartState;
    verifiedCartState = Object.freeze({ fingerprint, validUntil: normalizedUntil, revision: Number(verifiedCartState && verifiedCartState.revision || 0) + 1 });
    return verifiedCartState;
  }

  function verifiedCartFor(fingerprint, now) {
    const state = verifiedCartState;
    const timestamp = Number(now || Date.now());
    return state && state.fingerprint === String(fingerprint || "") && (!state.validUntil || state.validUntil > timestamp) ? state : null;
  }

  function expireVerifiedCart(fingerprint) {
    if (!verifiedCartState || fingerprint && verifiedCartState.fingerprint !== String(fingerprint)) return false;
    verifiedCartState = null;
    return true;
  }

  function hiddenCompanion(line) {
    return authoritativeProtectionProductIds.has(numericId(line && line.product_id));
  }

  const visibleQuantity = (cart) => (cart && cart.items || []).reduce((sum, line) =>
    hiddenCompanion(line) ? sum : sum + Math.max(0, Number(line.quantity || 0)), 0);

  function syncAuthoritativeCart(cart, doc) {
    doc = doc || document;
    const rawItemCount = Number(cart && cart.item_count || 0);
    const count = visibleQuantity(cart);
    const formatted = new Intl.NumberFormat(doc.documentElement.lang || "en-US", { style: "currency", currency: cart.currency || "USD" }).format(money(cart) / 100);
    doc.querySelectorAll(".wbhcartitem,[data-cart-count]").forEach((node) => {
      node.textContent = String(count);
      if (node.setAttribute) node.setAttribute("data-cart-count-raw", String(rawItemCount));
    });
    doc.querySelectorAll(".totals__subtotal").forEach((node) => {
      const copy = String(node.textContent || "");
      node.textContent = /\(\s*\d+\s*\)/.test(copy) ? copy.replace(/\(\s*\d+\s*\)/, `(${count})`) : copy;
      if (node.setAttribute) node.setAttribute("data-cart-count-raw", String(rawItemCount));
    });
    doc.querySelectorAll(".totals__subtotal-value").forEach((node) => { node.textContent = formatted; });
    const detail = { itemCount: count, visibleQuantity: count, rawItemCount, totalPrice: money(cart), currency: cart.currency || "USD", fingerprint: cartFingerprint(cart) };
    doc.dispatchEvent(new CustomEvent("gllf:authoritative-cart", { detail }));
    return { ...detail, formatted };
  }

  function expectedForGroup(expected, group) {
    return {
      ...expected,
      group,
      items: (expected.items || []).map((item) => ({
        ...item,
        properties: Object.fromEntries(Object.entries(item.properties || {}).map(([key, value]) => [key, key === "_Protection Group" ? group : value])),
      })),
    };
  }

  function expectedSelectionIds(expected) {
    return Array.from(new Set((expected.items || []).map((item) => String(item.properties && item.properties["_Protection Selection ID"] || "")).filter(Boolean))).sort();
  }

  function expectedQuoteIds(expected) {
    return Array.from(new Set((expected.items || []).flatMap((item) => {
      const properties = item.properties || {};
      return [properties["_Protection Quote ID"], ...String(properties["_Protection Quote IDs"] || "").split("|")];
    }).map(String).filter(Boolean))).sort();
  }

  function mutationId(expected) {
    if (expected && expected.mutationId) return String(expected.mutationId);
    const parent = (expected && expected.items || [])[0] || {};
    const quoteIds = (expected && expected.items || []).map((item) => String(item.properties && item.properties["_Protection Quote ID"] || "")).filter(Boolean).sort();
    return `gllf-protection-mutation-v1-${digest({ selectionIds: expectedSelectionIds(expected), quoteIds, variantId: numericId(parent.id), quantity: Number(parent.quantity || 0) })}`;
  }

  function matchingExpectedLine(actual, expected, group) {
    if (numericId(actual.variant_id || actual.id) !== numericId(expected.id)) return false;
    if (Number(actual.quantity) !== Number(expected.quantity)) return false;
    const required = expected.properties || {};
    return Object.keys(required).every((key) => String(required[key] == null ? "" : required[key]) === String(key === "_Protection Group" ? group : property(actual, key)));
  }

  function validateChargeLocally(charge, parent) {
    const contract = root && (root.GLLFProtectionContractV2 || root.GLLFProtectionContract);
    if (!contract || typeof contract.validateCharge !== "function") return { ok: false, code: "PROTECTION_CONTRACT_PENDING" };
    if (typeof contract.validateChargeDetailed === "function") return contract.validateChargeDetailed(charge, parent, Date.now());
    return { ok: Boolean(contract.validateCharge(charge, parent, Date.now())), code: "LEGACY_CONTRACT" };
  }

  function verifyProtectedCart(cart, expected) {
    const grouped = (cart && cart.items || []).filter((line) => property(line, "_Protection Group") === expected.group);
    const globallyCorrelated = (cart && cart.items || []).filter((line) => affectedLine(line, affectedContext(expected, [expected.group])));
    const parent = grouped.filter((line) => !property(line, "_Protection Type"));
    const children = grouped.filter((line) => property(line, "_Protection Type"));
    const remaining = grouped.slice();
    const missing = [];
    (expected.items || []).forEach((item) => {
      const index = remaining.findIndex((line) => matchingExpectedLine(line, item, expected.group));
      if (index < 0) missing.push(item);
      else remaining.splice(index, 1);
    });
    const unexpected = remaining;
    const issues = [];
    if (parent.length !== 1) issues.push("PARENT_CARDINALITY_MISMATCH");
    if (children.length !== Math.max(0, (expected.items || []).length - 1)) issues.push("CHILD_CARDINALITY_MISMATCH");
    if (missing.length) issues.push("EXPECTED_LINE_MISSING");
    if (unexpected.length) issues.push("UNEXPECTED_LINE_PRESENT");
    if (globallyCorrelated.length !== grouped.length || globallyCorrelated.some((line) => property(line, "_Protection Group") !== expected.group)) issues.push("GLOBAL_PROTECTION_CORRELATION_CONFLICT");
    Object.entries(expected.attributes || {}).forEach(([key, value]) => {
      if (String(cart && cart.attributes && cart.attributes[key] || "") !== String(value == null ? "" : value)) issues.push("CART_ATTRIBUTE_MISMATCH");
    });
    if (parent.length === 1) {
      children.forEach((child) => {
        const validation = validateChargeLocally(child, parent[0]);
        if (!validation.ok) issues.push(validation.code || "CHARGE_CONTRACT_INVALID");
        if (Number(child.final_line_price) !== Number(child.original_line_price)) issues.push("PROTECTION_DISCOUNT_APPLIED");
      });
    }
    const byQuote = new Map();
    children.forEach((line) => {
      const quoteId = property(line, "_Protection Quote ID");
      if (!byQuote.has(quoteId)) byQuote.set(quoteId, []);
      byQuote.get(quoteId).push(line);
    });
    byQuote.forEach((lines) => {
      const expectedAmount = Number(property(lines[0], "_Protection Quote Amount"));
      const actualAmount = lines.reduce((sum, line) => sum + Number(line.original_line_price || 0), 0);
      if (!Number.isSafeInteger(expectedAmount) || expectedAmount < 1 || actualAmount !== expectedAmount) issues.push("PROTECTION_AMOUNT_MISMATCH");
    });
    const expectedProtection = (expected.items || []).slice(1).reduce((sum, item) => sum + Math.max(0, Number(item.expectedLineCents || 0)), 0);
    const actualProtection = children.reduce((sum, line) => sum + Number(line.final_line_price != null ? line.final_line_price : line.original_line_price || 0), 0);
    if (expectedProtection > 0 && expectedProtection !== actualProtection) issues.push("EXPECTED_PROTECTION_TOTAL_MISMATCH");
    return { ok: issues.length === 0, code: issues[0] || "VERIFIED", issues: Array.from(new Set(issues)), parent, children, missing, unexpected, actualProtection };
  }

  async function json(fetchImpl, url, options) {
    const timerHost = root && typeof root.setTimeout === "function" ? root : (typeof setTimeout === "function" ? { setTimeout, clearTimeout } : null);
    const Abort = root && root.AbortController || (typeof AbortController === "function" ? AbortController : null);
    const controller = Abort && !(options && options.signal) ? new Abort() : null;
    const requestOptions = controller ? { ...(options || {}), signal: controller.signal } : options;
    const request = Promise.resolve().then(() => fetchImpl(url, requestOptions));
    let timeoutId = null;
    let response;
    if (timerHost) {
      const deadline = new Promise((resolve, reject) => {
        timeoutId = timerHost.setTimeout(() => {
          if (controller) controller.abort();
          const error = new Error("The cart request timed out before Shopify confirmed it.");
          error.code = "SHOPIFY_CART_REQUEST_TIMEOUT";
          reject(error);
        }, 10_000);
      });
      try { response = await Promise.race([request, deadline]); }
      finally { if (timeoutId != null) timerHost.clearTimeout(timeoutId); }
    } else response = await request;
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(body.description || body.message || `Cart request failed (${response.status}).`);
      error.code = body.code || "SHOPIFY_CART_REQUEST_FAILED";
      throw error;
    }
    return body;
  }

  function affectedContext(expected, groups) {
    const selections = new Set(expectedSelectionIds(expected));
    const quoteIds = new Set(expectedQuoteIds(expected));
    const groupIds = new Set((groups && groups.length ? groups : [expected.group]).map(String).filter(Boolean));
    const mutationIds = new Set([expected.mutationId, ...(expected.items || []).map((item) => item.properties && item.properties["_GLLF Cart Mutation ID"])].map(String).filter(Boolean));
    return { selections, quoteIds, groupIds, mutationIds };
  }

  function affectedLine(line, context) {
    const lineQuotes = [property(line, "_Protection Quote ID"), ...property(line, "_Protection Quote IDs").split("|")].filter(Boolean);
    return context.groupIds.has(property(line, "_Protection Group")) ||
      context.selections.has(property(line, "_Protection Selection ID")) ||
      context.mutationIds.has(property(line, "_GLLF Cart Mutation ID")) ||
      lineQuotes.some((quoteId) => context.quoteIds.has(quoteId));
  }

  function affectedProjection(cart, expected, attributeKeys, includeKeys, groups) {
    const context = affectedContext(expected, groups);
    const lines = (cart && cart.items || []).filter((line) => affectedLine(line, context)).map((line) => lineProjection(line, includeKeys));
    lines.sort((left, right) => canonical(left).localeCompare(canonical(right)));
    const attributes = {};
    attributeKeys.forEach((key) => { const value = String(cart && cart.attributes && cart.attributes[key] || ""); if (value) attributes[key] = value; });
    return { lines, attributes };
  }

  function locateExistingState(cart, expected) {
    const context = affectedContext(expected, [expected.group]);
    const lines = (cart.items || []).filter((line) => affectedLine(line, context));
    const groups = Array.from(new Set(lines.map((line) => property(line, "_Protection Group")).filter(Boolean))).sort();
    return { lines, groups, groupless: lines.some((line) => !property(line, "_Protection Group")) };
  }

  function lineRestorePayload(line) {
    const payload = { id: line.variant_id || line.id, quantity: Number(line.quantity || 0), properties: deepCopy(line.properties || {}) };
    const sellingPlan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
    if (sellingPlan && sellingPlan.id) payload.selling_plan = numericId(sellingPlan.id);
    return payload;
  }

  async function rollbackMutation(fetchImpl, before, expected, restoreAttributes) {
    const attributeKeys = Object.keys(restoreAttributes);
    const beforeAffected = affectedProjection(before, expected, attributeKeys, false);
    const context = affectedContext(expected, [expected.group]);
    const expectedVariants = new Set((expected.items || []).map((item) => numericId(item.id)).filter(Boolean));
    const variantProjection = (cart) => (cart && cart.items || []).filter((line) => expectedVariants.has(numericId(line.variant_id || line.id))).map((line) => lineProjection(line, false)).sort((left, right) => canonical(left).localeCompare(canonical(right)));
    const variantBefore = variantProjection(before);
    const operations = [];
    const failures = [];
    let current = null;
    try { current = await json(fetchImpl, "/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } }); }
    catch (error) { failures.push({ step: "READ_CURRENT", message: error.message }); }
    if (current) {
      const beforeByKey = new Map((before.items || []).map((line) => [String(line.key || ""), line]));
      const currentAffected = (current.items || []).filter((line) => affectedLine(line, context));
      const removals = {};
      currentAffected.forEach((line) => {
        if (!beforeByKey.has(String(line.key || "")) && line.key) removals[line.key] = 0;
      });
      if (Object.keys(removals).length) {
        try {
          await json(fetchImpl, "/cart/update.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ updates: removals }) });
          operations.push({ step: "REMOVE_ATTEMPT_LINES", keys: Object.keys(removals) });
        } catch (error) { failures.push({ step: "REMOVE_ATTEMPT_LINES", message: error.message }); }
      }
      for (const line of currentAffected) {
        const prior = beforeByKey.get(String(line.key || ""));
        if (!prior || canonical(lineProjection(prior, false)) === canonical(lineProjection(line, false))) continue;
        try {
          await json(fetchImpl, "/cart/change.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ id: line.key, quantity: prior.quantity, properties: prior.properties || {} }) });
          operations.push({ step: "RESTORE_MERGED_LINE", key: line.key });
        } catch (error) { failures.push({ step: "RESTORE_MERGED_LINE", key: line.key, message: error.message }); }
      }
      const currentKeys = new Set((current.items || []).map((line) => String(line.key || "")));
      const missingPrior = (before.items || []).filter((line) => affectedLine(line, context) && !currentKeys.has(String(line.key || "")));
      if (missingPrior.length) {
        try {
          await json(fetchImpl, "/cart/add.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: missingPrior.map(lineRestorePayload) }) });
          operations.push({ step: "RESTORE_MISSING_LINES", count: missingPrior.length });
        } catch (error) { failures.push({ step: "RESTORE_MISSING_LINES", message: error.message }); }
      }
    }
    if (attributeKeys.length) {
      try {
        await json(fetchImpl, "/cart/update.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ attributes: restoreAttributes }) });
        operations.push({ step: "RESTORE_ATTRIBUTES", keys: attributeKeys });
      } catch (error) { failures.push({ step: "RESTORE_ATTRIBUTES", message: error.message }); }
    }
    let finalCart = null;
    try { finalCart = await json(fetchImpl, "/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } }); }
    catch (error) { failures.push({ step: "VERIFY_ROLLBACK", message: error.message }); }
    const afterAffected = finalCart ? affectedProjection(finalCart, expected, attributeKeys, false) : null;
    const variantAfter = finalCart ? variantProjection(finalCart) : null;
    const rollbackVerified = Boolean(finalCart && canonical(beforeAffected) === canonical(afterAffected) && canonical(variantBefore) === canonical(variantAfter));
    const residualLines = finalCart ? affectedProjection(finalCart, expected, attributeKeys, true).lines : [];
    const suspectedVariantLines = finalCart && canonical(variantBefore) !== canonical(variantAfter) ? (finalCart.items || []).filter((line) => expectedVariants.has(numericId(line.variant_id || line.id))).map((line) => lineProjection(line, true)) : [];
    return {
      rollbackVerified,
      beforeFingerprint: cartFingerprint(before),
      beforeSemanticFingerprint: cartSemanticFingerprint(before),
      rollbackFingerprint: finalCart ? cartFingerprint(finalCart) : null,
      rollbackSemanticFingerprint: finalCart ? cartSemanticFingerprint(finalCart) : null,
      affectedBefore: beforeAffected,
      affectedAfter: afterAffected,
      expectedVariantStateBefore: variantBefore,
      expectedVariantStateAfter: variantAfter,
      residualLines,
      suspectedVariantLines,
      operations,
      failures,
      cart: finalCart,
    };
  }

  async function completeInterruptedMutation(fetchImpl, before, expected, audit) {
    const restoreAttributes = {};
    Object.keys(expected.attributes || {}).forEach((key) => {
      restoreAttributes[key] = Object.prototype.hasOwnProperty.call(before.attributes || {}, key) ? before.attributes[key] : "";
    });
    try {
      await json(fetchImpl, "/cart/update.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ attributes: expected.attributes || {} }) });
      if (audit.missing.length) {
        const missingItems = audit.missing.map(({ expectedLineCents, ...item }) => item);
        await json(fetchImpl, "/cart/add.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: missingItems }) });
      }
      const cart = await json(fetchImpl, "/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } });
      const completedAudit = verifyProtectedCart(cart, expected);
      if (!completedAudit.ok) {
        const error = new Error(`Shopify did not complete the interrupted protected purchase (${completedAudit.code}).`);
        error.code = completedAudit.code;
        throw error;
      }
      return { ok: true, cart, audit: completedAudit, rollback: null };
    } catch (cause) {
      const rollback = await rollbackMutation(fetchImpl, before, expected, restoreAttributes);
      return { ok: false, cause, cart: rollback.cart, audit: null, rollback };
    }
  }

  class CartMutationError extends Error {
    constructor(message, code, evidence, cause) {
      super(message);
      this.name = "CartMutationError";
      this.code = code;
      this.evidence = evidence;
      this.cause = cause;
    }
  }

  async function performProtectedMutation(input, id) {
    const fetchImpl = input.fetchImpl || fetch;
    const before = await json(fetchImpl, "/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } });
    const existing = locateExistingState(before, input);
    if (existing.lines.length) {
      if (existing.groups.length !== 1 || existing.groupless) throw new CartMutationError("This protection selection exists in more than one cart group or lacks its group identity. No additional lines were added.", "PROTECTION_MUTATION_CONFLICT", { mutationId: id, rollbackVerified: false, beforeFingerprint: cartFingerprint(before), groups: existing.groups, groupless: existing.groupless, residualLines: existing.lines.map((line) => lineProjection(line, true)) });
      const existingExpected = expectedForGroup(input, existing.groups[0]);
      const existingAudit = verifyProtectedCart(before, existingExpected);
      if (existingAudit.ok) return { before, cart: before, audit: existingAudit, mutationId: id, reused: true, rollbackVerified: null, visibleQuantity: visibleQuantity(before), totalPrice: money(before), fingerprint: cartFingerprint(before) };
      if (existingAudit.unexpected.length) throw new CartMutationError("A prior protection attempt contains changed or unexpected lines. No additional lines were added.", "PROTECTION_MUTATION_CONFLICT", { mutationId: id, rollbackVerified: false, beforeFingerprint: cartFingerprint(before), existingGroup: existing.groups[0], audit: existingAudit });
      const completion = await completeInterruptedMutation(fetchImpl, before, existingExpected, existingAudit);
      if (completion.ok) return { before, cart: completion.cart, audit: completion.audit, mutationId: id, reused: true, recovered: true, rollbackVerified: null, visibleQuantity: visibleQuantity(completion.cart), totalPrice: money(completion.cart), fingerprint: cartFingerprint(completion.cart) };
      const rollback = completion.rollback;
      const message = rollback && rollback.rollbackVerified ? "The prior protection attempt could not be completed; its exact partial state was restored." : "The prior protection attempt could not be completed and Shopify did not prove that its partial state was restored.";
      throw new CartMutationError(message, rollback && rollback.rollbackVerified ? "PROTECTION_INTERRUPTED_COMPLETION_FAILED" : "PROTECTION_ROLLBACK_UNVERIFIED", { mutationId: id, ...(rollback || { rollbackVerified: false }) }, completion.cause);
    }
    const restoreAttributes = {};
    Object.keys(input.attributes || {}).forEach((key) => {
      restoreAttributes[key] = Object.prototype.hasOwnProperty.call(before.attributes || {}, key) ? before.attributes[key] : "";
    });
    try {
      await json(fetchImpl, "/cart/update.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ attributes: input.attributes }) });
      const shopifyItems = input.items.map(({ expectedLineCents, ...item }) => item);
      await json(fetchImpl, "/cart/add.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: shopifyItems }) });
      const after = await json(fetchImpl, "/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } });
      const audit = verifyProtectedCart(after, input);
      if (!audit.ok) {
        const error = new Error(`Shopify did not retain the complete protected purchase (${audit.code}).`);
        error.code = audit.code;
        throw error;
      }
      return { before, cart: after, audit, mutationId: id, reused: false, rollbackVerified: null, visibleQuantity: visibleQuantity(after), totalPrice: money(after), fingerprint: cartFingerprint(after) };
    } catch (cause) {
      const rollback = await rollbackMutation(fetchImpl, before, input, restoreAttributes);
      const message = rollback.rollbackVerified
        ? (cause && cause.message || "The protected plant could not be verified in Shopify.")
        : "The protected cart attempt failed and Shopify did not prove a complete rollback. Open the cart to review the exact remaining lines before trying again.";
      throw new CartMutationError(message, rollback.rollbackVerified ? (cause.code || "PROTECTION_MUTATION_FAILED") : "PROTECTION_ROLLBACK_UNVERIFIED", { mutationId: id, ...rollback }, cause);
    }
  }

  function addVerifiedProtectedCart(input) {
    const id = mutationId(input);
    if (inFlight.has(id)) return inFlight.get(id);
    const markedInput = {
      ...input,
      mutationId: id,
      items: (input.items || []).map((item) => ({
        ...item,
        properties: { ...(item.properties || {}), "_GLLF Cart Mutation ID": id },
      })),
    };
    const operation = performProtectedMutation(markedInput, id).finally(() => inFlight.delete(id));
    inFlight.set(id, operation);
    return operation;
  }

  let headerSyncInstalled = false;
  let cartUpdateUnsubscribe = null;
  async function refreshVeenaHeader() {
    if (!root || typeof root.fetch !== "function" || typeof document === "undefined") return null;
    const routesRoot = root.Shopify && root.Shopify.routes && root.Shopify.routes.root || "/";
    try {
      const cart = await json(root.fetch.bind(root), `${String(routesRoot).replace(/\/?$/, "/")}cart.js`, { credentials: "same-origin", headers: { Accept: "application/json" } });
      return syncAuthoritativeCart(cart, document);
    } catch (_) { return null; }
  }

  function installVeenaHeaderSync() {
    if (headerSyncInstalled || typeof document === "undefined") return;
    headerSyncInstalled = true;
    const start = () => {
      refreshVeenaHeader();
      try {
        if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS === "object" && PUB_SUB_EVENTS.cartUpdate) {
          cartUpdateUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            const cart = event && event.cartData;
            if (cart && Array.isArray(cart.items)) syncAuthoritativeCart(cart, document);
            else refreshVeenaHeader();
          });
        }
      } catch (_) { cartUpdateUnsubscribe = null; }
    };
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
    else start();
  }

  const api = Object.freeze({
    contractVersion: "gllf.cart-authority.v2",
    addVerifiedProtectedCart,
    verifyProtectedCart,
    visibleQuantity,
    hiddenCompanion,
    money,
    syncAuthoritativeCart,
    cartFingerprint,
    cartSemanticFingerprint,
    cartProjection,
    mutationId,
    CartMutationError,
    rememberVerifiedCart,
    verifiedCartFor,
    expireVerifiedCart,
    installVeenaHeaderSync,
    refreshVeenaHeader,
  });
  installVeenaHeaderSync();
  return api;
});
